import { useCallback } from 'react';
import type { useQueryClient } from '@tanstack/react-query';
import type { ProcessDetailDto, ProcessGraphDto } from '@api/processes';
import { processDetailQueryKey } from './hooks/useProcessDetail';

/**
 * Module-level noop swallower for promise chains where the underlying
 * hook drives the user-visible failure surface (a status pill, a
 * modal, etc). Pulled out so call-sites stay a single statement.
 */
const noopErrorHandler = (): void => undefined;

/**
 * Tiny custom hook that bundles the two drift escape hatches surfaced
 * by the {@code DriftBlockerModal} into a single object. Lives in its
 * own file (rather than next to {@code ProcessEditorPage}) so the
 * React-fast-refresh rule against mixing component + hook exports
 * stays happy.
 *
 * The Discard path explicitly calls {@code setGraph} after the refetch
 * settles so the dirty canvas is replaced with the server's state.
 * {@code useProcessGraph}'s normal reseed-on-new-detail flow would be
 * blocked by its "don't clobber pending edits" guard ({@code !isDirty}
 * check), which is the correct behaviour for background refetches but
 * wrong here because the whole reason we're in this modal is that the
 * user is mid-edit and explicitly chose to throw their edits away
 * (AIB-6976 — silent edit loss during multi-tab drift recovery).
 *
 * Both handlers also arm {@code confirmDriftRecovery} on the autosave
 * hook BEFORE triggering their action. That flag is the only thing
 * the hook's drift-recovery effect respects when deciding to clear
 * {@code driftedRef} on a {@code detail.updatedAt} bump -- without
 * it, an unrelated invalidate (run-session events, publish, etc.)
 * would dismiss the drift state silently and the canvas would
 * un-lock under the user. On the failure path (invalidate rejected,
 * forceSave threw before the mutation ran) we call
 * {@code cancelDriftRecovery} to revoke the arming so a subsequent
 * passive refetch can't sneak it through.
 */
export const useDriftHandlers = (
  processId: string | undefined,
  queryClient: ReturnType<typeof useQueryClient>,
  forceSave: () => Promise<unknown>,
  setGraph: (graph: ProcessGraphDto) => void,
  confirmDriftRecovery: () => void,
  cancelDriftRecovery: () => void,
): { onRefresh: () => void; onForceSave: () => void } => {
  const onRefresh = useCallback(() => {
    if (!processId) return;
    confirmDriftRecovery();
    const queryKey = processDetailQueryKey(processId);
    queryClient
      .invalidateQueries({ queryKey })
      .then(() => {
        const fresh = queryClient.getQueryData<ProcessDetailDto>(queryKey);
        if (!fresh) return;
        // Force-reseed the canvas with the server state, bypassing
        // useProcessGraph's dirty guard. setGraph also resets isDirty
        // + clears the undo history so the user can't "undo" their
        // way back into the drifted state.
        setGraph({ nodes: fresh.nodes, edges: fresh.edges });
      })
      .catch(() => {
        // Invalidate rejected (offline, abort, etc). The drift modal
        // stays mounted so the user can retry; revoke the arming
        // here so an unrelated subsequent invalidate can't piggy-
        // back on this confirmation.
        cancelDriftRecovery();
      });
  }, [
    processId,
    queryClient,
    setGraph,
    confirmDriftRecovery,
    cancelDriftRecovery,
  ]);
  const onForceSave = useCallback(() => {
    confirmDriftRecovery();
    forceSave().catch(() => {
      // forceSave swallowed the visible surface via the autosave
      // status machine; we just need to revoke the arming so a
      // later passive refetch doesn't auto-dismiss the modal that
      // the failure left mounted. (The mutation's onError also
      // clears the flag -- this is a belt-and-braces for the path
      // where forceSave's pre-mutation guards reject without
      // running the mutation.)
      cancelDriftRecovery();
      noopErrorHandler();
    });
  }, [forceSave, confirmDriftRecovery, cancelDriftRecovery]);
  return { onRefresh, onForceSave };
};
